/**
 * Created by Jerker Nord on 2021-09-21.
 */
import {TrueGritSymbol, TrueGritSymbolStates} from "./TrueGritSymbol";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {TrueGrit} from "../TrueGrit";
import {gsap, TimelineLite} from "gsap";
import {TrueGritStatics} from "../TrueGritStatics";
import {TrueGritReelStopAnimationRunner} from "../presentationrunners/maingame/TrueGritReelStopAnimationRunner";
import {TrueGritSound} from "../sound/TrueGritSound";
import {TrueGritGameMode} from "../server/TrueGritGameMode";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {TrueGritModel} from "../TrueGritModel";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";

export class TrueGritScatterSymbol extends TrueGritSymbol {

    private static SHOOTING_ANIMATION_OFFSET:PIXI.Point = new PIXI.Point(79, -68);
    private static SPLIT_2_ANIMATION_OFFSET:PIXI.Point = new PIXI.Point(75, -48);
    private static SPLIT_4_ANIMATION_OFFSET:PIXI.Point = new PIXI.Point(75.5, -47.5);

    private _shootingAnimation:TimelineSprite;
    private _split2Animation:TimelineSprite;
    private _split4Animation:TimelineSprite;
    private _landedInThisRound:boolean = false;
    private _isUsed:boolean = false;
    private _shootingAnimations:TimelineLite[];
    private _shootingAnimationComplete:boolean = false;
    private _shootingAnimationStarted:boolean = false;
    private _plusFSImage: PIXI.Sprite;
    private _landedInThisOutcomeForFS: boolean = false;
    private _waitForAnticipation:boolean = false;

    constructor(symbolName:string) {
        super(symbolName);
        this.createAnimations();
    }

    private createAnimations():void {
        const shootingAnimationTextures:PIXI.Texture[] = TrueGrit.assetLoader.getTextures("S/S_shoot/sShoot_");
        this._shootingAnimation = new TimelineSprite(shootingAnimationTextures);
        this._shootingAnimation.anchor.set(0.5, 0.5);
        this._shootingAnimation.visible = false;
        TrueGrit.scatterAnimationView.addChild(this._shootingAnimation);

        //Push animation to GPU
        for(let i:number = 0; i < shootingAnimationTextures.length; i++) {
            TrueGrit.gpuPushView.addChild(new PIXI.Sprite(shootingAnimationTextures[i]));
        }

        this._shootingAnimations = [];
        // 1-4 shots
        this._shootingAnimations.push(this._shootingAnimation.getAnimation([23, 56]).timeScale(1.6));//23., 30
        this._shootingAnimations.push(this._shootingAnimation.getAnimation([23, 39]).timeScale(1.6));
        this._shootingAnimations.push(this._shootingAnimation.getAnimation([23, 48]).timeScale(1.6));
        this._shootingAnimations.push(this._shootingAnimation.getAnimation([23, 56]).timeScale(1.6));
        this._shootingAnimations.reverse();

        const split2AnimationTextures:PIXI.Texture[] = TrueGrit.assetLoader.getTextures("S/2/S_2split_");
        this._split2Animation = new TimelineSprite(split2AnimationTextures);
        this._split2Animation.anchor.set(0.5, 0.5);
        this._split2Animation.visible = false;
        TrueGrit.scatterAnimationView.addChild(this._split2Animation);
        //Push animation to GPU
        for(let i:number = 0; i < split2AnimationTextures.length; i++) {
            TrueGrit.gpuPushView.addChild(new PIXI.Sprite(split2AnimationTextures[i]));
        }

        const split4AnimationTextures:PIXI.Texture[] = TrueGrit.assetLoader.getTextures("S/4/S_4split_");
        this._split4Animation = new TimelineSprite(split4AnimationTextures);
        this._split4Animation.anchor.set(0.5, 0.5);
        this._split4Animation.visible = false;
        TrueGrit.scatterAnimationView.addChild(this._split4Animation);
        //Push animation to GPU
        for(let i:number = 0; i < split4AnimationTextures.length; i++) {
            TrueGrit.gpuPushView.addChild(new PIXI.Sprite(split4AnimationTextures[i]));
        }

        this.createPlusFSItem();
    }

    public getSplitAnimation(newSplitLevel:number):gsap.core.Timeline {
        const splitTL:gsap.core.Timeline = gsap.timeline(),
            isGameModeFS: boolean = (TrueGrit.model.gameData.mode.indexOf(TrueGritGameMode.FREESPIN) > -1);

        // #340
        if (isGameModeFS && !this._landedInThisOutcomeForFS) {
            return splitTL;
        }

        splitTL.add(() => {this._splitLevel = newSplitLevel});
        if(newSplitLevel == 2) {
            this._split2Animation.position.set(TrueGritStatics.reelPositions[this.reelId].x + TrueGritScatterSymbol.SPLIT_2_ANIMATION_OFFSET.x,
                (this.reelTargetPosition * TrueGritStatics.symbolHeight) + TrueGritScatterSymbol.SPLIT_2_ANIMATION_OFFSET.y);
            splitTL.add([
                ()=>{TrueGrit.soundPlayer.playSound(TrueGritSound.SCATTER_SPLIT)},
                this._split2Animation.getAnimationAutoShowHide(),
                () => this.visible = false
            ]);
        } else {
            this._split4Animation.position.set(TrueGritStatics.reelPositions[this.reelId].x + TrueGritScatterSymbol.SPLIT_4_ANIMATION_OFFSET.x,
                (this.reelTargetPosition * TrueGritStatics.symbolHeight) + TrueGritScatterSymbol.SPLIT_4_ANIMATION_OFFSET.y);
            splitTL.add([
                ()=>{TrueGrit.soundPlayer.playSound(TrueGritSound.SCATTER_SPLIT_2)},
                this._split4Animation.getAnimationAutoShowHide(),
                () => {this.visible = false}
            ]);
        }
        splitTL.add([
            () => {this.visible = true},
            () => {this.setState(TrueGritSymbolStates.NORMAL, "")}
        ]);

        // #295 -> either FREESPIN or FREESPIN_AVALANCHE
        if (isGameModeFS) {
            splitTL.add(this.playPlusFSAnimation(true));
            splitTL.add(() => {
                let previousFSLeft: number = TrueGritModel.previousFSLeft;

                previousFSLeft += (newSplitLevel === 2 ? 1 : 2);

                SlotGame.keypadPlugin.setZeroBetSpinCounter(Math.max(previousFSLeft, 0));
                TrueGritModel.previousFSLeft = previousFSLeft;
            }, "-=0.2");
        }

        return splitTL;
    }

    public playScatterWaitingDoneAnimation():void {
        this._shootingAnimationStarted = true;
        TrueGritReelStopAnimationRunner.reelStopAnimationStarted();
        this._landedInThisRound = true;
        const scatterLandedTimeline:gsap.core.Timeline = gsap.timeline();
        scatterLandedTimeline.add(() => {TrueGrit.symbolBoxView.getRemoveBoxAnimation().restart()});
        scatterLandedTimeline.add(this.getNumberOfShotsAnimation());
        scatterLandedTimeline.add(() => {this._shootingAnimationComplete = true});
        scatterLandedTimeline.add([
            this._shootingAnimation.getAnimation([78, 91]).timeScale(1.6),
            () => {TrueGrit.soundPlayer.playSound(TrueGritSound.SCATTER_SPIN_BACK_SOUND)}
        ]);
        scatterLandedTimeline.add(() => {this._shootingAnimation.visible = false});
        scatterLandedTimeline.add(() => {this.visible = true});
        scatterLandedTimeline.add(() => {this.getStateAnimation(TrueGritSymbolStates.COLLAPSE_LANDING, 0.2)}, "-=0.2");
        scatterLandedTimeline.add(() => TrueGritReelStopAnimationRunner.reelStopAnimationStopped());
        //scatterLandedTimeline.add(() => TrueGrit.trueGritScatterSymbols.checkToPlayEndAnimation());

        // #295 either FREESPIN or FREESPIN_AVALANCHE
        if (TrueGrit.model.gameData.mode.indexOf(TrueGritGameMode.FREESPIN) > -1) {
            scatterLandedTimeline.add([
                this.playPlusFSAnimation(true),
                () => {
                    TrueGritModel.previousFSLeft += 2;
                    SlotGame.keypadPlugin.setZeroBetSpinCounter(Math.max(TrueGritModel.previousFSLeft, 0));
                }
            ]);

            this._landedInThisOutcomeForFS = true;
        }
    }

    public playScatterLandedAnimation():void {
        if(this._landedInThisRound == false) {
            let count = 0;
            let landingSound = TrueGritSound.SCATTER_LANDED_SOUND;

            if (TrueGrit.model.data.mode == TrueGritGameMode.FREESPIN || TrueGrit.model.data.mode == TrueGritGameMode.FREESPIN_AVALANCHE){
                landingSound = TrueGritSound.SCATTER_4_LANDED_SOUND;
            } else {
                for (let pos of TrueGrit.model.allScatterPositions){
                    count++;
                    if (pos.equals(new PIXI.Point(this.reelId, this.reelTargetPosition)) && count > 2){
                        landingSound = TrueGritSound.SCATTER_4_LANDED_SOUND;
                    }
                }
            }

            TrueGrit.soundPlayer.playSound(landingSound);


      /*      if(this.reelTargetPosition == TrueGrit.model.superFSScatterPosition.y && this.reelId == TrueGrit.model.superFSScatterPosition.x) {
                TrueGrit.soundPlayer.playSound(TrueGritSound.SCATTER_4_LANDED_SOUND);
            } else {
                TrueGrit.soundPlayer.playSound(TrueGritSound.SCATTER_LANDED_SOUND);
            }*/

            if (this.waitForAnticipation == false){
                this._shootingAnimationStarted = true;
                TrueGritReelStopAnimationRunner.reelStopAnimationStarted();
                this._landedInThisRound = true;
                const scatterLandedTimeline:gsap.core.Timeline = gsap.timeline();
                scatterLandedTimeline.add(() => {
                    this._shootingAnimation.position.set(TrueGritStatics.reelPositions[this.reelId].x + TrueGritScatterSymbol.SHOOTING_ANIMATION_OFFSET.x,
                        (this.reelTargetPosition * TrueGritStatics.symbolHeight) + TrueGritScatterSymbol.SHOOTING_ANIMATION_OFFSET.y)
                });
                scatterLandedTimeline.add(() => {this.visible = false});
                scatterLandedTimeline.add(() => {this._shootingAnimation.visible = true});
                scatterLandedTimeline.add(this._shootingAnimation.getAnimation([0, 22]).timeScale(3.2));
                scatterLandedTimeline.add(() => {TrueGrit.symbolBoxView.getRemoveBoxAnimation().restart()});
                scatterLandedTimeline.add(this.getNumberOfShotsAnimation());
                scatterLandedTimeline.add(() => {this._shootingAnimationComplete = true});
                scatterLandedTimeline.add([
                    this._shootingAnimation.getAnimation([78, 91]).timeScale(1.6),
                    () => {TrueGrit.soundPlayer.playSound(TrueGritSound.SCATTER_SPIN_BACK_SOUND)}
                ]);
                scatterLandedTimeline.add(() => {this._shootingAnimation.visible = false});
                scatterLandedTimeline.add(() => {this.visible = true});
                scatterLandedTimeline.add(() => {this.getStateAnimation(TrueGritSymbolStates.COLLAPSE_LANDING, 0.2)}, "-=0.2");
                scatterLandedTimeline.add(() => TrueGritReelStopAnimationRunner.reelStopAnimationStopped());
                //scatterLandedTimeline.add(() => TrueGrit.trueGritScatterSymbols.checkToPlayEndAnimation());

                // #295 either FREESPIN or FREESPIN_AVALANCHE
                if (TrueGrit.model.gameData.mode.indexOf(TrueGritGameMode.FREESPIN) > -1) {
                    scatterLandedTimeline.add([
                        this.playPlusFSAnimation(true),
                        () => {
                            TrueGritModel.previousFSLeft += 2;
                            SlotGame.keypadPlugin.setZeroBetSpinCounter(Math.max(TrueGritModel.previousFSLeft, 0));
                        }
                    ]);

                    this._landedInThisOutcomeForFS = true;
                }
            }else{
                const scatterLandedTimeline:gsap.core.Timeline = gsap.timeline();
                scatterLandedTimeline.add(() => {
                    this._shootingAnimation.position.set(TrueGritStatics.reelPositions[this.reelId].x + TrueGritScatterSymbol.SHOOTING_ANIMATION_OFFSET.x,
                        (this.reelTargetPosition * TrueGritStatics.symbolHeight) + TrueGritScatterSymbol.SHOOTING_ANIMATION_OFFSET.y)
                });
                scatterLandedTimeline.add(() => {this.visible = false});
                scatterLandedTimeline.add(() => {this._shootingAnimation.visible = true});
                scatterLandedTimeline.add(this._shootingAnimation.getAnimation([0, 22]).timeScale(3.2));
            }
        }
    }

    // #295
    public playPlusFSAnimation(isGameModeFS:boolean):TimelineLite {
        const tl:TimelineLite = new TimelineLite();

        if(this._splitLevel < 2 && !isGameModeFS) {
            tl.to(this, {duration : 0.001});

            return tl;
        }

        tl.add(() => {TrueGrit.soundPlayer.playSound(TrueGritSound.SCATTER_PLUS_SPINS)});
        tl.add(() => {
            let numPlusFS:number = 0;

            if(this._splitLevel === 2) {
                numPlusFS += 1;
            } else if(this._splitLevel === 4) {
                numPlusFS += (isGameModeFS ? 2 : 3);
            }

            if(numPlusFS === 0 && isGameModeFS) {
                numPlusFS += 2;
            }

            if(numPlusFS !== 0) {
                this._plusFSImage.texture = TrueGrit.assetLoader.getTextures("S/S_+" + numPlusFS)[0];
                this._plusFSImage.position.set(TrueGritStatics.reelPositions[this.reelId].x + 72, (this.y + 72));
                this._plusFSImage.alpha = 0;
                this._plusFSImage.visible = true;
            }
        }, 0);

        tl.to(this._plusFSImage, {
            duration : 0.3,
            alpha : 1
        }, 0);

        tl.add(() => {gsap.to(this._plusFSImage, {
            duration : 0.3,
            alpha : 0,
            y : (this._plusFSImage.y - 72),
            onComplete : () => {this._plusFSImage.visible = false}
        })}, 0.6);

        tl.to(this, {duration : 0.2}, 0.6);

        return tl;
    }

    /*
    public playSpinToEndAnimation():void {
        const spinToEndTimeline:gsap.core.Timeline = gsap.timeline();
        spinToEndTimeline.add(this._shootingAnimation.getAnimation([78, 91]).timeScale(1.2));
        spinToEndTimeline.add(() => this._shootingAnimation.visible = false);
        spinToEndTimeline.add(() => this.visible = true);
        spinToEndTimeline.add(() => this.getStateAnimation(TrueGritSymbolStates.COLLAPSE_LANDING, 0.2), "-=0.2");
        spinToEndTimeline.add(() => TrueGritReelStopAnimationRunner.reelStopAnimationStopped());
    }

     */

    private getNumberOfShotsAnimation():TimelineLite {
        return this._shootingAnimations[TrueGrit.trueGritScatterSymbols.getScattersLanded() - 1];
    }

    public reset():void {
        this._landedInThisRound = false;
        this._isUsed = false;

        // #340 -> this might have been false earlier, just to be on safe side.
        this._landedInThisOutcomeForFS = false;
    }

    public animateCollapse():gsap.core.Timeline {
        const timeline:gsap.core.Timeline = gsap.timeline();
        timeline.add([
            gsap.to(this.position, {
                duration : 0.1 * (this.reelTargetPosition - this.reelPosition),
                y : TrueGritStatics.symbolHeight * (this.reelTargetPosition - 1) + 1,
                ease : "power3.in"
            }),
            gsap.to(this._shootingAnimation.position, {
                duration : 0.1 * (this.reelTargetPosition - this.reelPosition),
                y : TrueGritStatics.symbolHeight * (this.reelTargetPosition - 1) + 70,
                ease : "power3.in"
            })
        ]);
        // #363, #437
        timeline.add(gsap.to(this.position, {
            duration : 0.03,
            y : TrueGritStatics.symbolHeight * (this.reelTargetPosition - 1) - 2,
            ease : "none"
        }));
        timeline.add(gsap.to(this.position, {
            duration : 0.05,
            y : TrueGritStatics.symbolHeight * (this.reelTargetPosition - 1),
            ease : "none"
        }));
        return timeline;
    }

    get isUsed():boolean {
        return this._isUsed;
    }

    set isUsed(value:boolean) {
        this._isUsed = value;
    }

    get landedInThisRound():boolean {
        return this._landedInThisRound;
    }

    get shootingAnimationComplete():boolean {
        return this._shootingAnimationComplete;
    }

    set shootingAnimationComplete(value:boolean) {
        this._shootingAnimationComplete = value;
    }

    get shootingAnimationStarted():boolean {
        return this._shootingAnimationStarted;
    }

    get waitForAnticipation():boolean {
        return this._waitForAnticipation;
    }

    set waitForAnticipation(value:boolean) {
        this._waitForAnticipation = value;
    }

    set shootingAnimationStarted(value:boolean) {
        this._shootingAnimationStarted = value;
    }

    // #340
    public set landedInThisOutcomeForFS(value:boolean) {
        this._landedInThisOutcomeForFS = value;
    }

    // #295
    private createPlusFSItem():void {
        this._plusFSImage = new PIXI.Sprite(PIXI.Texture.EMPTY);
        this._plusFSImage.visible = false;
        this._plusFSImage.anchor.set(0.5);

        TrueGrit.scatterAnimationView.addChild(this._plusFSImage);
    }
}